import React, {
  useState,
  useEffect,
  useRef,
  SelectHTMLAttributes,
} from 'react';
import styled from '@emotion/styled';
import { rem } from 'polished';
import { css } from '@emotion/react';
import {
  animations,
  colors,
  spaces,
  text,
} from '../../../core/styles/theme/default';
import { IconSVG } from '../../../primitives';
import ErrorMessage from '../ErrorMessage';
import { RequireOnlyOne } from '../../../types/utils';

const StyledSelectWrap = styled.div`
  position: relative;
`;

const StyledSelectTrigger = styled.div(
  ({ isDisabled, isFilled, hasError }: any) => css`
    position: absolute;
    top: 0;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: ${rem(56)};
    padding: 0 ${rem(spaces.delta)};
    border-radius: ${rem(2)};
    color: ${isFilled ? colors.grey900 : colors.grey700};
    border: 1px solid ${colors.grue300};
    background-color: ${colors.grue100};
    outline: none;
    pointer-events: none;
    transition: all ${animations.default};
    ${text.p3};

    ${isDisabled &&
    css`
      background-color: ${colors.grey300};
    `}
    ${hasError &&
    css`
      border-color: ${colors.error};
    `};
    ${isFilled &&
    css`
      span > span {
        display: none;
      }
    `}
  `,
);

const StyledIcon = styled(IconSVG)`
  path {
    stroke: ${colors.black};
  }
`;

const StyledAsterisks = styled.span`
  color: ${colors.primary};
  margin-left: ${rem(spaces.alpha)};
`;

const StyledSelect = styled.select`
  display: block;
  width: 100%;
  height: ${rem(56)};
  outline: none;
  cursor: pointer;
  border-radius: ${rem(2)};

  &:disabled {
    cursor: default;
  }

  &:hover,
  &:focus {
    text-overflow: unset;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.06), 0px 4px 6px rgba(0, 0, 0, 0.1);
  }
`;

const Select = ({
  label,
  options,
  errorText,
  onChangeRequest,
  changeValueOnRequest,
  className,
  value,
  ...rest
}: SelectProps) => {
  const hasError = !!errorText;
  const { required: isRequired, disabled: isDisabled } = rest;
  const [selectVal, setSelectVal] = useState(value || '');
  const [triggerLabel, setTriggerLabel] = useState<string>();
  const [isFilled, setIsFilled] = useState<boolean>(false);
  const selectRef = useRef(null);

  useEffect(() => {
    if (selectRef.current) {
      const {
        current: { options },
      }: any = selectRef;

      const optionLabel = options[options.selectedIndex].label;
      setTriggerLabel(optionLabel);
      setIsFilled(optionLabel !== label);
    } else {
      setTriggerLabel(label);
    }

    if (changeValueOnRequest) {
      setSelectVal(changeValueOnRequest.value);
      setTriggerLabel(changeValueOnRequest.label);
    }
  }, [changeValueOnRequest, label, selectRef]);

  const handleChange = (event: any) => {
    const triggerLabel = event.target[event.target.selectedIndex].text;
    setSelectVal(event.target.value);
    setTriggerLabel(triggerLabel);
    setIsFilled(triggerLabel !== label);

    onChangeRequest && onChangeRequest(event);
  };

  return options.length > 0 ? (
    <StyledSelectWrap className={className}>
      <StyledSelect
        ref={selectRef}
        value={selectVal}
        disabled={isDisabled}
        required={isRequired}
        onChange={handleChange}
        {...rest}
      >
        {label && (
          <option value="" disabled>
            {label}
          </option>
        )}
        {options.map((option: any) => {
          return (
            <option value={option.value} key={option.value}>
              {option.label}
            </option>
          );
        })}
      </StyledSelect>
      <StyledSelectTrigger
        isFilled={isFilled}
        isDisabled={isDisabled}
        hasError={hasError}
      >
        <span>
          {triggerLabel}
          {isRequired && <StyledAsterisks>*</StyledAsterisks>}
        </span>
        <StyledIcon iconName="EchIconDown" />
      </StyledSelectTrigger>
      {errorText && <ErrorMessage text={errorText} />}
    </StyledSelectWrap>
  ) : null;
};

interface SelectInterface extends SelectHTMLAttributes<HTMLSelectElement> {
  /** Label of the Select */
  label?: string;
  /** Array of Select Options */
  options: Array<any>;
  /** Error Text to be shown */
  errorText?: string;
  /** Class of the Select */
  className?: string;
  /** Optional Callback fired when the Select changes. For example usefull to get the value of the Select */
  onChangeRequest?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  /** This object can be set if the value needs to be change from outside of the Select (ex. from a button click) */
  changeValueOnRequest?: { value: any; label: string };
}

export type SelectProps = RequireOnlyOne<SelectInterface, 'label' | 'value'>;

export default Select;
